import React from "react";

const MODES = [
  {
    key: "percent",
    label: "Procent",
    hint: "Procent od (Wynagrodzenie − Dopłata paliwowa)",
    fields: ["percent"],
  },
  {
    key: "rate",
    label: "Stała stawka",
    hint: "Stała kwota za okres (bez wpływu dokumentu)",
    fields: ["rate"],
  },
  {
    key: "rate_percent",
    label: "Stawka + %",
    hint: "Stała stawka + procent od (Wynagrodzenie − Dopłata paliwowa)",
    fields: ["rate", "percent"],
  },
  {
    key: "dniowki",
    label: "Dniówki",
    hint: "Sotrudnik wpisuje dni pracy, admin zatwierdza — stawka × liczba dni",
    fields: ["daily_rate"],
  },
];

/**
 * Reusable config editor for `salary_config` on a user.
 * Purely controlled — parent owns state.
 *
 * Props:
 *   value  = { mode, percent, rate, daily_rate }
 *   onChange(next) — merged patch, parent uses spread to update
 *   compact = smaller layout for modals
 */
export default function SalaryConfigSection({ value, onChange, compact = false }) {
  const v = value || { mode: "percent", percent: 50, rate: 0, daily_rate: 0 };
  const set = (patch) => onChange({ ...v, ...patch });
  const active = MODES.find((m) => m.key === v.mode) || MODES[0];

  return (
    <div className="space-y-3" data-testid="salary-config-section">
      <div className={`grid ${compact ? "grid-cols-2" : "grid-cols-4"} gap-2`}>
        {MODES.map((m) => (
          <label
            key={m.key}
            className={`cursor-pointer border rounded p-2 text-sm transition-colors ${
              v.mode === m.key ? "border-[#2563EB] bg-blue-50" : "border-slate-200 hover:bg-slate-50"
            }`}
            data-testid={`salary-mode-${m.key}`}
          >
            <input
              type="radio"
              name="salary-mode"
              className="hidden"
              checked={v.mode === m.key}
              onChange={() => set({ mode: m.key })}
            />
            <div className="font-semibold">{m.label}</div>
            <div className="text-[11px] text-slate-500 mt-0.5">{m.hint}</div>
          </label>
        ))}
      </div>

      <div className="grid grid-cols-2 gap-2">
        {active.fields.includes("percent") && (
          <div>
            <label className="field-label block mb-1">Procent (%)</label>
            <input
              type="number"
              min={1}
              max={100}
              step={1}
              value={v.percent ?? 50}
              onChange={(e) => set({ percent: parseFloat(e.target.value || 0) })}
              className="input-field"
              data-testid="salary-percent-input"
            />
          </div>
        )}
        {active.fields.includes("rate") && (
          <div>
            <label className="field-label block mb-1">Stała stawka (zł / okres)</label>
            <input
              type="number"
              min={0}
              step={0.01}
              value={v.rate ?? 0}
              onChange={(e) => set({ rate: parseFloat(e.target.value || 0) })}
              className="input-field"
              data-testid="salary-rate-input"
            />
          </div>
        )}
        {active.fields.includes("daily_rate") && (
          <div>
            <label className="field-label block mb-1">Stawka dzienna (zł / dzień)</label>
            <input
              type="number"
              min={0}
              step={0.01}
              value={v.daily_rate ?? 0}
              onChange={(e) => set({ daily_rate: parseFloat(e.target.value || 0) })}
              className="input-field"
              data-testid="salary-daily-rate-input"
            />
          </div>
        )}
      </div>
    </div>
  );
}

/** Compact badge shown in employee lists. */
export function SalaryModeBadge({ config }) {
  const mode = config?.mode || "percent";
  const map = {
    percent:      { label: `${config?.percent ?? 50}%`,           cls: "bg-blue-100 text-blue-800",      title: "Procent" },
    rate:         { label: `${(config?.rate ?? 0).toLocaleString("pl-PL")} zł`, cls: "bg-purple-100 text-purple-800", title: "Stała stawka" },
    rate_percent: { label: `${(config?.rate ?? 0).toLocaleString("pl-PL")} zł + ${config?.percent ?? 50}%`, cls: "bg-indigo-100 text-indigo-800", title: "Stawka + %" },
    dniowki:      { label: `${(config?.daily_rate ?? 0).toLocaleString("pl-PL")} zł/dz.`, cls: "bg-amber-100 text-amber-800", title: "Dniówki" },
  };
  const b = map[mode] || map.percent;
  return (
    <span
      className={`inline-block text-[10px] font-mono px-1.5 py-0.5 rounded ${b.cls}`}
      title={b.title}
      data-testid={`salary-mode-badge-${mode}`}
    >
      {b.label}
    </span>
  );
}
